import { toast } from "react-toastify";
import { useDispatch, useSelector } from "react-redux";
import { useState, useEffect, useRef, useMemo, FormEvent } from "react";
import dayjs from "dayjs";
import { Dialog } from "@mui/material";
import citiesData from "data/cities.json";
import prefecturesData from "data/prefectures.json";
import workOptionsData from "data/workOptions.json";
import { useDialog } from "app/hook";
import { DialogType } from "app/types";
import { RootState, AppDispatch } from "app/store";
import { useScale, usePlaySound } from "common/utils";
import { ParentCloseBtn, FadeTransition, Select } from "common/elements";
import i18n from "features/parent/i18n";
import { updateAccountProfile } from "features/parent/api";
import {
  ParentGender,
  Glasses,
  Hairstyle,
  BodyType,
  FaceShape,
  AccountProfile,
} from "features/parent/types";

export type ProfileEditSection = "basic" | "region" | "work" | "family" | "appearance";

interface ProfileEditModalProps {
  open: boolean;
  section: ProfileEditSection;
  onClose: () => void;
}

const householdMemberOptions = [
  { value: "mother", label: "MSG_HOUSEHOLD_MEMBER_MOTHER" },
  { value: "father", label: "MSG_HOUSEHOLD_MEMBER_FATHER" },
  { value: "grandmother", label: "MSG_HOUSEHOLD_MEMBER_GRANDMOTHER" },
  { value: "grandfather", label: "MSG_HOUSEHOLD_MEMBER_GRANDFATHER" },
];

const userRoleOptions = [
  { value: "mother", label: "MSG_HOUSEHOLD_MEMBER_MOTHER" },
  { value: "father", label: "MSG_HOUSEHOLD_MEMBER_FATHER" },
  { value: "grandmother", label: "MSG_HOUSEHOLD_MEMBER_GRANDMOTHER" },
  { value: "grandfather", label: "MSG_HOUSEHOLD_MEMBER_GRANDFATHER" },
];

const GLASSES_OPTIONS = [
  { key: "none", labelKey: "MSG_GLASSES_NONE_BY_PARENT" },
  { key: "glasses", labelKey: "MSG_GLASSES_YES_BY_PARENT" },
];

const HAIRSTYLE_OPTIONS = [
  { key: "very_short", labelKey: "MSG_HAIRSTYLE_VERY_SHORT_BY_PARENT" },
  { key: "short", labelKey: "MSG_HAIRSTYLE_SHORT_BY_PARENT" },
  { key: "medium", labelKey: "MSG_HAIRSTYLE_MEDIUM_BY_PARENT" },
  { key: "slightly_long", labelKey: "MSG_HAIRSTYLE_SLIGHTLY_LONG_BY_PARENT" },
  { key: "long", labelKey: "MSG_HAIRSTYLE_LONG_BY_PARENT" },
];

const BODY_TYPE_OPTIONS = [
  { key: "thin", labelKey: "MSG_BODY_TYPE_THIN_BY_PARENT" },
  { key: "slightly_thin", labelKey: "MSG_BODY_TYPE_SLIGHTLY_THIN_BY_PARENT" },
  { key: "medium", labelKey: "MSG_BODY_TYPE_MEDIUM_BY_PARENT" },
  { key: "slightly_chubby", labelKey: "MSG_BODY_TYPE_SLIGHTLY_CHUBBY_BY_PARENT" },
  { key: "chubby", labelKey: "MSG_BODY_TYPE_CHUBBY_BY_PARENT" },
];

const FACE_SHAPE_OPTIONS = [
  { key: "round", labelKey: "MSG_FACE_SHAPE_ROUND_BY_PARENT" },
  { key: "oval", labelKey: "MSG_FACE_SHAPE_OVAL_BY_PARENT" },
  { key: "oblong", labelKey: "MSG_FACE_SHAPE_OBLONG_BY_PARENT" },
  { key: "square", labelKey: "MSG_FACE_SHAPE_SQUARE_BY_PARENT" },
  { key: "triangle", labelKey: "MSG_FACE_SHAPE_TRIANGLE_BY_PARENT" },
];

const getSectionTitle = (section: ProfileEditSection): string => {
  switch (section) {
    case "basic":
      return i18n.t("MSG_PARENT_PAGE_PROFILE_BASIC_INFO");
    case "region":
      return i18n.t("MSG_PARENT_PROFILE_REGION");
    case "work":
      return i18n.t("MSG_PARENT_PROFILE_WORK");
    case "family":
      return i18n.t("MSG_PARENT_PROFILE_FAMILY");
    case "appearance":
      return i18n.t("MSG_PARENT_PROFILE_APPEARANCE");
  }
};

export const ProfileEditModal = ({ open, section, onClose }: ProfileEditModalProps) => {
  const { scale } = useScale();
  const play = usePlaySound();
  const dispatch = useDispatch<AppDispatch>();
  const handleDialog = useDialog();
  const parent = useSelector((state: RootState) => state.user.parent);
  const parentProfile = useSelector((state: RootState) => state.parent.parentProfile);

  const [birthday, setBirthday] = useState("");
  const [birthdayYear, setBirthdayYear] = useState<number | null>(null);
  const [birthdayMonth, setBirthdayMonth] = useState<number | null>(null);
  const [birthdayDay, setBirthdayDay] = useState<number | null>(null);
  const [gender, setGender] = useState<ParentGender>(ParentGender.MALE);
  const [region, setRegion] = useState("");
  const [city, setCity] = useState("");
  const [householdMembers, setHouseholdMembers] = useState<string[]>([]);
  const [boysCount, setBoysCount] = useState<string>("");
  const [girlsCount, setGirlsCount] = useState<string>("");
  const [occupation, setOccupation] = useState("");
  const [industry, setIndustry] = useState("");
  const [jobTitle, setJobTitle] = useState("");
  const [annualIncome, setAnnualIncome] = useState<string>("");
  const [userRole, setUserRole] = useState<string>("");
  const [glasses, setGlasses] = useState<string>("");
  const [hairstyle, setHairstyle] = useState<string>("");
  const [bodyType, setBodyType] = useState<string>("");
  const [faceShape, setFaceShape] = useState<string>("");
  const [selfIntroduction, setSelfIntroduction] = useState("");

  // 生年月日 Select 用
  const [yearOpen, setYearOpen] = useState(false);
  const [monthOpen, setMonthOpen] = useState(false);
  const [dayOpen, setDayOpen] = useState(false);
  const yearRef = useRef<HTMLButtonElement>(null);
  const monthRef = useRef<HTMLButtonElement>(null);
  const dayRef = useRef<HTMLButtonElement>(null);

  // 地域 Select 用
  const [prefectureOpen, setPrefectureOpen] = useState(false);
  const [cityOpen, setCityOpen] = useState(false);
  const prefectureRef = useRef<HTMLButtonElement>(null);
  const cityRef = useRef<HTMLButtonElement>(null);

  // お勤め先 Select 用
  const [industryOpen, setIndustryOpen] = useState(false);
  const [occupationOpen, setOccupationOpen] = useState(false);
  const [jobTitleOpen, setJobTitleOpen] = useState(false);
  const [incomeOpen, setIncomeOpen] = useState(false);
  const industryRef = useRef<HTMLButtonElement>(null);
  const occupationRef = useRef<HTMLButtonElement>(null);
  const jobTitleRef = useRef<HTMLButtonElement>(null);
  const incomeRef = useRef<HTMLButtonElement>(null);

  // 家族構成 Select 用
  const [userRoleOpen, setUserRoleOpen] = useState(false);
  const [boysCountOpen, setBoysCountOpen] = useState(false);
  const [girlsCountOpen, setGirlsCountOpen] = useState(false);
  const userRoleRef = useRef<HTMLButtonElement>(null);
  const boysCountRef = useRef<HTMLButtonElement>(null);
  const girlsCountRef = useRef<HTMLButtonElement>(null);

  // 容姿 Select 用
  const [glassesOpen, setGlassesOpen] = useState(false);
  const [hairstyleOpen, setHairstyleOpen] = useState(false);
  const [bodyTypeOpen, setBodyTypeOpen] = useState(false);
  const [faceShapeOpen, setFaceShapeOpen] = useState(false);
  const glassesRef = useRef<HTMLButtonElement>(null);
  const hairstyleRef = useRef<HTMLButtonElement>(null);
  const bodyTypeRef = useRef<HTMLButtonElement>(null);
  const faceShapeRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    if (parentProfile) {
      const existingBirthday = parentProfile.birthday;
      if (existingBirthday) {
        const d = dayjs(existingBirthday);
        if (d.isValid() && d.year() >= 1920) {
          setBirthday(d.format("YYYY-MM-DD"));
          setBirthdayYear(d.year());
          setBirthdayMonth(d.month() + 1);
          setBirthdayDay(d.date());
        } else {
          setBirthday("");
          setBirthdayYear(null);
          setBirthdayMonth(null);
          setBirthdayDay(null);
        }
      } else {
        setBirthday("");
        setBirthdayYear(null);
        setBirthdayMonth(null);
        setBirthdayDay(null);
      }
      setGender(parentProfile.gender ?? ParentGender.MALE);
      setRegion(parentProfile.region ?? "");
      setCity(parentProfile.city ?? "");
      setHouseholdMembers(parentProfile.household_members ?? []);
      setBoysCount(
        parentProfile.boys_count !== null && parentProfile.boys_count !== undefined
          ? String(parentProfile.boys_count)
          : "",
      );
      setGirlsCount(
        parentProfile.girls_count !== null && parentProfile.girls_count !== undefined
          ? String(parentProfile.girls_count)
          : "",
      );
      setOccupation(parentProfile.occupation ?? "");
      setIndustry(parentProfile.industry ?? "");
      setJobTitle(parentProfile.job_title ?? "");
      setAnnualIncome(parentProfile.annual_income ?? "");
      setUserRole(parentProfile.user_role ?? "");
      setGlasses(parentProfile.glasses ?? "");
      setHairstyle(parentProfile.hairstyle ?? "");
      setBodyType(parentProfile.body_type ?? "");
      setFaceShape(parentProfile.face_shape ?? "");
      setSelfIntroduction(parentProfile.self_introduction ?? "");
    }
  }, [parentProfile]);

  // 都道府県・市区町村オプション
  const prefectureOptions = useMemo(
    () => prefecturesData.prefectures.map((pref) => ({ value: pref.code, label: pref.name })),
    [],
  );

  const cityOptions = useMemo(() => {
    if (!region) return [];
    const cities = (citiesData as Record<string, { code: string; name: string }[]>)[region] || [];
    return cities.map((c) => ({ value: c.code, label: c.name }));
  }, [region]);

  // お勤め先オプション
  const industryOptions = useMemo(
    () => workOptionsData.industry.map((opt) => ({ value: opt.key, label: i18n.t(opt.labelKey) })),
    [],
  );
  const occupationOptions = useMemo(
    () =>
      workOptionsData.occupation.map((opt) => ({ value: opt.key, label: i18n.t(opt.labelKey) })),
    [],
  );
  const jobTitleOptions = useMemo(
    () => workOptionsData.jobTitle.map((opt) => ({ value: opt.key, label: i18n.t(opt.labelKey) })),
    [],
  );
  const incomeOptions = useMemo(
    () => [
      { value: "under_5m", label: i18n.t("MSG_PARENT_ONBOARDING_INCOME_UNDER_5M") },
      { value: "5m_10m", label: i18n.t("MSG_PARENT_ONBOARDING_INCOME_5M_10M") },
      { value: "over_10m", label: i18n.t("MSG_PARENT_ONBOARDING_INCOME_OVER_10M") },
      {
        value: "prefer_not_to_say",
        label: i18n.t("MSG_PARENT_ONBOARDING_INCOME_PREFER_NOT_TO_SAY"),
      },
    ],
    [],
  );

  // 容姿オプション
  const glassesOptions = useMemo(
    () => GLASSES_OPTIONS.map((opt) => ({ value: opt.key, label: i18n.t(opt.labelKey) })),
    [],
  );
  const hairstyleOptions = useMemo(
    () => HAIRSTYLE_OPTIONS.map((opt) => ({ value: opt.key, label: i18n.t(opt.labelKey) })),
    [],
  );
  const bodyTypeOptions = useMemo(
    () => BODY_TYPE_OPTIONS.map((opt) => ({ value: opt.key, label: i18n.t(opt.labelKey) })),
    [],
  );
  const faceShapeOptions = useMemo(
    () => FACE_SHAPE_OPTIONS.map((opt) => ({ value: opt.key, label: i18n.t(opt.labelKey) })),
    [],
  );

  const handleClose = () => {
    play();
    onClose();
  };

  // バリデーション：セクションに応じた必須フィールドが設定されているか
  const isValid = (): boolean => {
    switch (section) {
      case "basic":
        return !!gender && !!birthdayYear && !!birthdayMonth && !!birthdayDay;
      case "region":
        return !!region && !!city;
      case "work":
        return !!occupation && !!jobTitle && !!industry && !!annualIncome;
      case "family":
        return !!userRole && !!boysCount && !!girlsCount;
      case "appearance":
        return !!glasses && !!hairstyle && !!bodyType && !!faceShape;
      default:
        return true;
    }
  };

  const handleSubmit = (e: FormEvent) => {
    e.preventDefault();
    if (!parent?.uid) return;

    let profile: Partial<AccountProfile> = {};

    switch (section) {
      case "basic":
        profile = {
          gender: gender,
          birthday: birthday || null,
        };
        break;
      case "region":
        profile = {
          region: region || null,
          city: city || null,
        };
        break;
      case "work":
        profile = {
          occupation: occupation || null,
          industry: industry || null,
          job_title: jobTitle || null,
          annual_income: (annualIncome || null) as AccountProfile["annual_income"],
        };
        break;
      case "family":
        profile = {
          user_role: userRole || null,
          household_members: householdMembers.length > 0 ? householdMembers : null,
          boys_count: boysCount ? parseInt(boysCount, 10) : null,
          girls_count: girlsCount ? parseInt(girlsCount, 10) : null,
        };
        break;
      case "appearance":
        profile = {
          glasses: (glasses as Glasses) || null,
          hairstyle: (hairstyle as Hairstyle) || null,
          body_type: (bodyType as BodyType) || null,
          face_shape: (faceShape as FaceShape) || null,
          self_introduction: selfIntroduction || null,
        };
        break;
    }

    handleDialog({ type: DialogType.LOADING, value: true });
    dispatch(updateAccountProfile({ uid: parent.uid, profile }))
      .unwrap()
      .then(() => {
        toast(
          <div>
            <p className="text text-textcolor-black">
              {i18n.t("MSG_PARENT_PAGE_PROFILE_UPDATE_SUCCESS")}
            </p>
          </div>,
        );
        onClose();
      })
      .catch((error) => {
        console.error(error);
        toast(
          <div>
            <p className="text text-textcolor-black">
              {i18n.t("MSG_PARENT_PAGE_PROFILE_UPDATE_ERROR")}
            </p>
          </div>,
        );
      })
      .finally(() => handleDialog({ type: DialogType.LOADING, value: false }));
  };

  const inputClass =
    "flex-1 box-border px-4 py-3 rounded-[8px] border border-gray2-60 text-[24px] text-textcolor-black bg-white focus:outline-none focus:border-ink transition-all";
  const _selectClass =
    "flex-1 box-border px-4 py-3 rounded-[8px] border border-gray2-60 text-[24px] text-textcolor-black bg-white focus:outline-none focus:border-ink transition-all appearance-none cursor-pointer";
  const labelClass = "text-[24px] text-textcolor-black w-[120px] shrink-0";

  const renderBasicSection = () => {
    // 生年月日オプション生成
    const currentYear = dayjs().year();
    const selectedYear = birthdayYear;
    const selectedMonth = birthdayMonth;
    const selectedDay = birthdayDay;

    const yearOptions = Array.from({ length: currentYear - 1920 + 1 }, (_, idx) => ({
      value: currentYear - idx,
      label: `${currentYear - idx}${i18n.t("MSG_PARENT_PAGE_YEAR")}`,
    }));

    const monthOptions = Array.from({ length: 12 }, (_, idx) => ({
      value: idx + 1,
      label: `${idx + 1}${i18n.t("MSG_PARENT_PAGE_MONTH")}`,
    }));

    const daysInMonth =
      selectedYear && selectedMonth
        ? dayjs(`${selectedYear}-${selectedMonth}-01`).daysInMonth()
        : 31;
    const dayOptions = Array.from({ length: daysInMonth }, (_, idx) => ({
      value: idx + 1,
      label: `${idx + 1}${i18n.t("MSG_PARENT_PAGE_DAY")}`,
    }));

    const handleYearChange = (year: number) => {
      const month = selectedMonth;
      const day = selectedDay;
      let validDay = day;
      if (month && day) {
        const maxDay = dayjs(`${year}-${month}-01`).daysInMonth();
        validDay = Math.min(day, maxDay);
        setBirthdayDay(validDay);
      }
      setBirthdayYear(year);
      if (month && validDay) {
        setBirthday(dayjs(`${year}-${month}-${validDay}`).format("YYYY-MM-DD"));
      } else {
        setBirthday("");
      }
    };

    const handleMonthChange = (month: number) => {
      const year = selectedYear;
      const day = selectedDay;
      let validDay = day;
      if (year && day) {
        const maxDay = dayjs(`${year}-${month}-01`).daysInMonth();
        validDay = Math.min(day, maxDay);
        setBirthdayDay(validDay);
      }
      setBirthdayMonth(month);
      if (year && validDay) {
        setBirthday(dayjs(`${year}-${month}-${validDay}`).format("YYYY-MM-DD"));
      } else {
        setBirthday("");
      }
    };

    const handleDayChange = (day: number) => {
      const year = selectedYear;
      const month = selectedMonth;
      setBirthdayDay(day);
      if (year && month) {
        setBirthday(dayjs(`${year}-${month}-${day}`).format("YYYY-MM-DD"));
      } else {
        setBirthday("");
      }
    };

    const buttonClass =
      "flex items-center justify-between gap-2 py-3 px-4 rounded-[8px] text-[24px] cursor-pointer select-none active:scale-95 bg-white border-2 border-gray2-60 text-textcolor-black";

    return (
      <div className="flex-col-el gap-4">
        {/* 性別 */}
        <div className="flex-row-el items-center gap-4">
          <label className={labelClass}>{i18n.t("MSG_PROFILE_SUBSTEP_GENDER_BY_PARENT")}</label>
          <div className="flex-row-el gap-8 flex-1">
            <div
              onClick={() => {
                play();
                setGender(ParentGender.MALE);
              }}
              className={`flex-col-el items-center justify-center w-[200px] h-[200px] cursor-pointer select-none active:scale-95 rounded-[8px] ${
                gender === ParentGender.MALE ? "bg-blue-100" : "bg-gray2-40"
              }`}
            >
              <span className="material-symbols-outlined !text-6xl text-white mb-3">man</span>
              <span className="text-[24px] text-white">
                {i18n.t("MSG_PARENT_ONBOARDING_GENDER_MALE")}
              </span>
            </div>
            <div
              onClick={() => {
                play();
                setGender(ParentGender.FEMALE);
              }}
              className={`flex-col-el items-center justify-center w-[200px] h-[200px] cursor-pointer select-none active:scale-95 rounded-[8px] ${
                gender === ParentGender.FEMALE ? "bg-red-100" : "bg-gray2-40"
              }`}
            >
              <span className="material-symbols-outlined !text-6xl text-white mb-3">woman</span>
              <span className="text-[24px] text-white">
                {i18n.t("MSG_PARENT_ONBOARDING_GENDER_FEMALE")}
              </span>
            </div>
          </div>
        </div>
        {/* 生年月日 */}
        <div className="flex-row-el items-center gap-4">
          <label className={labelClass}>{i18n.t("MSG_PROFILE_SUBSTEP_BIRTHDAY_BY_PARENT")}</label>
          <div className="flex-row-el gap-2 flex-1">
            {/* 年 */}
            <button
              type="button"
              ref={yearRef}
              onClick={() => {
                play();
                setYearOpen(true);
              }}
              className={`${buttonClass} w-[160px]`}
            >
              <span>
                {selectedYear
                  ? `${selectedYear}${i18n.t("MSG_PARENT_PAGE_YEAR")}`
                  : i18n.t("MSG_PARENT_PAGE_YEAR")}
              </span>
              <span className="material-symbols-outlined !text-[24px]">expand_more</span>
            </button>
            <Select
              anchorRef={yearRef}
              open={yearOpen}
              onClose={() => setYearOpen(false)}
              value={selectedYear}
              onChange={handleYearChange}
              options={yearOptions}
            />
            {/* 月 */}
            <button
              type="button"
              ref={monthRef}
              onClick={() => {
                play();
                setMonthOpen(true);
              }}
              className={`${buttonClass} w-[130px]`}
            >
              <span>
                {selectedMonth
                  ? `${selectedMonth}${i18n.t("MSG_PARENT_PAGE_MONTH")}`
                  : i18n.t("MSG_PARENT_PAGE_MONTH")}
              </span>
              <span className="material-symbols-outlined !text-[24px]">expand_more</span>
            </button>
            <Select
              anchorRef={monthRef}
              open={monthOpen}
              onClose={() => setMonthOpen(false)}
              value={selectedMonth}
              onChange={handleMonthChange}
              options={monthOptions}
            />
            {/* 日 */}
            <button
              type="button"
              ref={dayRef}
              onClick={() => {
                play();
                setDayOpen(true);
              }}
              className={`${buttonClass} w-[130px]`}
            >
              <span>
                {selectedDay
                  ? `${selectedDay}${i18n.t("MSG_PARENT_PAGE_DAY")}`
                  : i18n.t("MSG_PARENT_PAGE_DAY")}
              </span>
              <span className="material-symbols-outlined !text-[24px]">expand_more</span>
            </button>
            <Select
              anchorRef={dayRef}
              open={dayOpen}
              onClose={() => setDayOpen(false)}
              value={selectedDay}
              onChange={handleDayChange}
              options={dayOptions}
            />
          </div>
        </div>
      </div>
    );
  };

  const renderRegionSection = () => {
    const buttonClass =
      "flex items-center justify-between gap-2 py-3 px-4 rounded-[8px] text-[24px] cursor-pointer select-none bg-white border-4 border-input-focus text-textcolor-black w-full";

    return (
      <div className="flex-col-el gap-4">
        {/* 都道府県 */}
        <div className="flex-row-el items-center gap-4">
          <label className={labelClass}>{i18n.t("MSG_PREFECTURE_TITLE")}</label>
          <div className="flex-1">
            <button
              type="button"
              ref={prefectureRef}
              onClick={() => {
                play();
                setPrefectureOpen(true);
              }}
              className={buttonClass}
            >
              <span>
                {region
                  ? prefectureOptions.find((o) => o.value === region)?.label
                  : i18n.t("MSG_PARENT_ONBOARDING_BIRTHDAY_SELECT")}
              </span>
              <span className="material-symbols-outlined !text-[24px]">expand_more</span>
            </button>
            <Select
              anchorRef={prefectureRef}
              open={prefectureOpen}
              onClose={() => setPrefectureOpen(false)}
              value={region || ""}
              onChange={(value) => {
                play();
                setRegion(value || "");
                setCity("");
              }}
              options={prefectureOptions}
            />
          </div>
        </div>
        {/* 市区町村 */}
        <div className="flex-row-el items-center gap-4">
          <label className={labelClass}>{i18n.t("MSG_CITY_TITLE")}</label>
          <div className="flex-1">
            <button
              type="button"
              ref={cityRef}
              onClick={() => {
                play();
                setCityOpen(true);
              }}
              className={buttonClass}
              disabled={!region}
            >
              <span>
                {city
                  ? cityOptions.find((o) => o.value === city)?.label
                  : i18n.t("MSG_PARENT_ONBOARDING_BIRTHDAY_SELECT")}
              </span>
              <span className="material-symbols-outlined !text-[24px]">expand_more</span>
            </button>
            <Select
              anchorRef={cityRef}
              open={cityOpen}
              onClose={() => setCityOpen(false)}
              value={city || ""}
              onChange={(value) => {
                play();
                setCity(value || "");
              }}
              options={cityOptions}
            />
          </div>
        </div>
      </div>
    );
  };

  const renderWorkSection = () => {
    const buttonClass =
      "flex items-center justify-between gap-2 py-3 px-4 rounded-[8px] text-[24px] cursor-pointer select-none bg-white border-4 border-input-focus text-textcolor-black w-full";

    return (
      <div className="flex-col-el gap-4">
        {/* 職種 */}
        <div className="flex-row-el items-center gap-4">
          <label className={labelClass}>{i18n.t("MSG_OCCUPATION_TITLE")}</label>
          <div className="flex-1">
            <button
              type="button"
              ref={occupationRef}
              onClick={() => {
                play();
                setOccupationOpen(true);
              }}
              className={buttonClass}
            >
              <span>
                {occupation
                  ? occupationOptions.find((o) => o.value === occupation)?.label
                  : i18n.t("MSG_PARENT_ONBOARDING_BIRTHDAY_SELECT")}
              </span>
              <span className="material-symbols-outlined !text-[24px]">expand_more</span>
            </button>
            <Select
              anchorRef={occupationRef}
              open={occupationOpen}
              onClose={() => setOccupationOpen(false)}
              value={occupation || ""}
              onChange={(value) => {
                play();
                setOccupation(value || "");
              }}
              options={occupationOptions}
            />
          </div>
        </div>
        {/* 役職 */}
        <div className="flex-row-el items-center gap-4">
          <label className={labelClass}>{i18n.t("MSG_JOB_TITLE_LABEL")}</label>
          <div className="flex-1">
            <button
              type="button"
              ref={jobTitleRef}
              onClick={() => {
                play();
                setJobTitleOpen(true);
              }}
              className={buttonClass}
            >
              <span>
                {jobTitle
                  ? jobTitleOptions.find((o) => o.value === jobTitle)?.label
                  : i18n.t("MSG_PARENT_ONBOARDING_BIRTHDAY_SELECT")}
              </span>
              <span className="material-symbols-outlined !text-[24px]">expand_more</span>
            </button>
            <Select
              anchorRef={jobTitleRef}
              open={jobTitleOpen}
              onClose={() => setJobTitleOpen(false)}
              value={jobTitle || ""}
              onChange={(value) => {
                play();
                setJobTitle(value || "");
              }}
              options={jobTitleOptions}
            />
          </div>
        </div>
        {/* 業種 */}
        <div className="flex-row-el items-center gap-4">
          <label className={labelClass}>{i18n.t("MSG_INDUSTRY_TITLE")}</label>
          <div className="flex-1">
            <button
              type="button"
              ref={industryRef}
              onClick={() => {
                play();
                setIndustryOpen(true);
              }}
              className={buttonClass}
            >
              <span>
                {industry
                  ? industryOptions.find((o) => o.value === industry)?.label
                  : i18n.t("MSG_PARENT_ONBOARDING_BIRTHDAY_SELECT")}
              </span>
              <span className="material-symbols-outlined !text-[24px]">expand_more</span>
            </button>
            <Select
              anchorRef={industryRef}
              open={industryOpen}
              onClose={() => setIndustryOpen(false)}
              value={industry || ""}
              onChange={(value) => {
                play();
                setIndustry(value || "");
              }}
              options={industryOptions}
            />
          </div>
        </div>
        {/* 年収 */}
        <div className="flex-row-el items-center gap-4">
          <label className={labelClass}>{i18n.t("MSG_PARENT_ONBOARDING_INCOME_TITLE")}</label>
          <div className="flex-1">
            <button
              type="button"
              ref={incomeRef}
              onClick={() => {
                play();
                setIncomeOpen(true);
              }}
              className={buttonClass}
            >
              <span>
                {annualIncome
                  ? incomeOptions.find((o) => o.value === annualIncome)?.label
                  : i18n.t("MSG_PARENT_ONBOARDING_BIRTHDAY_SELECT")}
              </span>
              <span className="material-symbols-outlined !text-[24px]">expand_more</span>
            </button>
            <Select
              anchorRef={incomeRef}
              open={incomeOpen}
              onClose={() => setIncomeOpen(false)}
              value={annualIncome || ""}
              onChange={(value) => {
                play();
                setAnnualIncome(value || "");
              }}
              options={incomeOptions}
            />
          </div>
        </div>
      </div>
    );
  };

  const renderFamilySection = () => {
    const buttonClass =
      "flex items-center justify-between gap-2 py-3 px-4 rounded-[8px] text-[24px] cursor-pointer select-none bg-white border-4 border-input-focus text-textcolor-black w-full";

    const userRoleSelectOptions = userRoleOptions.map((opt) => ({
      value: opt.value,
      label: i18n.t(opt.label),
    }));

    const countOptions = [0, 1, 2, 3, 4, 5].map((n) => ({
      value: String(n),
      label: `${n}人`,
    }));

    return (
      <div className="flex-col-el gap-4">
        {/* 自分の役割 */}
        <div className="flex-row-el items-center gap-4">
          <label className={labelClass}>{i18n.t("MSG_USER_ROLE_TITLE")}</label>
          <div className="flex-1">
            <button
              type="button"
              ref={userRoleRef}
              onClick={() => {
                play();
                setUserRoleOpen(true);
              }}
              className={buttonClass}
            >
              <span>
                {userRole
                  ? userRoleSelectOptions.find((o) => o.value === userRole)?.label
                  : i18n.t("MSG_PARENT_ONBOARDING_BIRTHDAY_SELECT")}
              </span>
              <span className="material-symbols-outlined !text-[24px]">expand_more</span>
            </button>
            <Select
              anchorRef={userRoleRef}
              open={userRoleOpen}
              onClose={() => setUserRoleOpen(false)}
              value={userRole || ""}
              onChange={(value) => {
                play();
                setUserRole(value || "");
              }}
              options={userRoleSelectOptions}
            />
          </div>
        </div>

        {/* 同居人（タブボタン形式） */}
        <div className="flex-row-el items-center gap-4">
          <label className={labelClass}>{i18n.t("MSG_HOUSEHOLD_MEMBERS_TITLE")}</label>
          <div className="flex-row-el flex-wrap gap-3">
            {householdMemberOptions.map((opt) => {
              const isActive = householdMembers.includes(opt.value);
              return (
                <div
                  key={opt.value}
                  onClick={() => {
                    play();
                    if (isActive) {
                      setHouseholdMembers(householdMembers.filter((m) => m !== opt.value));
                    } else {
                      setHouseholdMembers([...householdMembers, opt.value]);
                    }
                  }}
                  className={`py-3 px-6 rounded-[10px] border-2 text-[21px] cursor-pointer select-none transition-all ${
                    isActive
                      ? "bg-green-alt border-green-alt text-textcolor-black"
                      : "bg-surface border-green-alt text-textcolor-black/70"
                  }`}
                >
                  {i18n.t(opt.label)}
                </div>
              );
            })}
          </div>
        </div>

        {/* 男の子 */}
        <div className="flex-row-el items-center gap-4">
          <label className={labelClass}>{i18n.t("MSG_BOYS_COUNT")}</label>
          <div className="flex-1">
            <button
              type="button"
              ref={boysCountRef}
              onClick={() => {
                play();
                setBoysCountOpen(true);
              }}
              className={buttonClass}
            >
              <span>
                {boysCount
                  ? countOptions.find((o) => o.value === boysCount)?.label
                  : i18n.t("MSG_PARENT_ONBOARDING_BIRTHDAY_SELECT")}
              </span>
              <span className="material-symbols-outlined !text-[24px]">expand_more</span>
            </button>
            <Select
              anchorRef={boysCountRef}
              open={boysCountOpen}
              onClose={() => setBoysCountOpen(false)}
              value={boysCount || ""}
              onChange={(value) => {
                play();
                setBoysCount(value || "");
              }}
              options={countOptions}
            />
          </div>
        </div>

        {/* 女の子 */}
        <div className="flex-row-el items-center gap-4">
          <label className={labelClass}>{i18n.t("MSG_GIRLS_COUNT")}</label>
          <div className="flex-1">
            <button
              type="button"
              ref={girlsCountRef}
              onClick={() => {
                play();
                setGirlsCountOpen(true);
              }}
              className={buttonClass}
            >
              <span>
                {girlsCount
                  ? countOptions.find((o) => o.value === girlsCount)?.label
                  : i18n.t("MSG_PARENT_ONBOARDING_BIRTHDAY_SELECT")}
              </span>
              <span className="material-symbols-outlined !text-[24px]">expand_more</span>
            </button>
            <Select
              anchorRef={girlsCountRef}
              open={girlsCountOpen}
              onClose={() => setGirlsCountOpen(false)}
              value={girlsCount || ""}
              onChange={(value) => {
                play();
                setGirlsCount(value || "");
              }}
              options={countOptions}
            />
          </div>
        </div>
      </div>
    );
  };

  const renderAppearanceSection = () => {
    const buttonClass =
      "flex items-center justify-between gap-2 py-3 px-4 rounded-[8px] text-[24px] cursor-pointer select-none bg-white border-4 border-input-focus text-textcolor-black w-full";

    return (
      <div className="flex-col-el gap-4">
        {/* メガネ */}
        <div className="flex-row-el items-center gap-4">
          <label className={labelClass}>{i18n.t("MSG_GLASSES_TITLE_BY_PARENT")}</label>
          <div className="flex-1">
            <button
              type="button"
              ref={glassesRef}
              onClick={() => {
                play();
                setGlassesOpen(true);
              }}
              className={buttonClass}
            >
              <span>
                {glasses
                  ? glassesOptions.find((o) => o.value === glasses)?.label
                  : i18n.t("MSG_PARENT_ONBOARDING_BIRTHDAY_SELECT")}
              </span>
              <span className="material-symbols-outlined !text-[24px]">expand_more</span>
            </button>
            <Select
              anchorRef={glassesRef}
              open={glassesOpen}
              onClose={() => setGlassesOpen(false)}
              value={glasses || ""}
              onChange={(value) => {
                play();
                setGlasses(value || "");
              }}
              options={glassesOptions}
            />
          </div>
        </div>
        {/* 髪型 */}
        <div className="flex-row-el items-center gap-4">
          <label className={labelClass}>{i18n.t("MSG_HAIRSTYLE_TITLE_BY_PARENT")}</label>
          <div className="flex-1">
            <button
              type="button"
              ref={hairstyleRef}
              onClick={() => {
                play();
                setHairstyleOpen(true);
              }}
              className={buttonClass}
            >
              <span>
                {hairstyle
                  ? hairstyleOptions.find((o) => o.value === hairstyle)?.label
                  : i18n.t("MSG_PARENT_ONBOARDING_BIRTHDAY_SELECT")}
              </span>
              <span className="material-symbols-outlined !text-[24px]">expand_more</span>
            </button>
            <Select
              anchorRef={hairstyleRef}
              open={hairstyleOpen}
              onClose={() => setHairstyleOpen(false)}
              value={hairstyle || ""}
              onChange={(value) => {
                play();
                setHairstyle(value || "");
              }}
              options={hairstyleOptions}
            />
          </div>
        </div>
        {/* 体型 */}
        <div className="flex-row-el items-center gap-4">
          <label className={labelClass}>{i18n.t("MSG_BODY_TYPE_TITLE_BY_PARENT")}</label>
          <div className="flex-1">
            <button
              type="button"
              ref={bodyTypeRef}
              onClick={() => {
                play();
                setBodyTypeOpen(true);
              }}
              className={buttonClass}
            >
              <span>
                {bodyType
                  ? bodyTypeOptions.find((o) => o.value === bodyType)?.label
                  : i18n.t("MSG_PARENT_ONBOARDING_BIRTHDAY_SELECT")}
              </span>
              <span className="material-symbols-outlined !text-[24px]">expand_more</span>
            </button>
            <Select
              anchorRef={bodyTypeRef}
              open={bodyTypeOpen}
              onClose={() => setBodyTypeOpen(false)}
              value={bodyType || ""}
              onChange={(value) => {
                play();
                setBodyType(value || "");
              }}
              options={bodyTypeOptions}
            />
          </div>
        </div>
        {/* 顔の形 */}
        <div className="flex-row-el items-center gap-4">
          <label className={labelClass}>{i18n.t("MSG_FACE_SHAPE_TITLE_BY_PARENT")}</label>
          <div className="flex-1">
            <button
              type="button"
              ref={faceShapeRef}
              onClick={() => {
                play();
                setFaceShapeOpen(true);
              }}
              className={buttonClass}
            >
              <span>
                {faceShape
                  ? faceShapeOptions.find((o) => o.value === faceShape)?.label
                  : i18n.t("MSG_PARENT_ONBOARDING_BIRTHDAY_SELECT")}
              </span>
              <span className="material-symbols-outlined !text-[24px]">expand_more</span>
            </button>
            <Select
              anchorRef={faceShapeRef}
              open={faceShapeOpen}
              onClose={() => setFaceShapeOpen(false)}
              value={faceShape || ""}
              onChange={(value) => {
                play();
                setFaceShape(value || "");
              }}
              options={faceShapeOptions}
            />
          </div>
        </div>
        {/* 自己紹介 */}
        <div className="flex-row-el items-start gap-4">
          <label className={labelClass}>{i18n.t("MSG_PARENT_PAGE_PROFILE_NOTES")}</label>
          <div className="flex-col-el flex-1">
            <textarea
              value={selfIntroduction}
              onChange={(e) => setSelfIntroduction(e.target.value)}
              className={`${inputClass} resize-none`}
              maxLength={100}
              rows={3}
            />
            <p className="text-body text-right text-[14px] text-gray2-80 mt-1">
              {i18n.t("MSG_BLOCKLY_INPUT_LENGTH_LIMIT", { limit: 100 })}
            </p>
          </div>
        </div>
      </div>
    );
  };

  const renderSectionContent = () => {
    switch (section) {
      case "basic":
        return renderBasicSection();
      case "region":
        return renderRegionSection();
      case "work":
        return renderWorkSection();
      case "family":
        return renderFamilySection();
      case "appearance":
        return renderAppearanceSection();
    }
  };

  return (
    <Dialog
      fullScreen
      open={open}
      maxWidth={false}
      sx={{ zIndex: 99999 }}
      TransitionComponent={FadeTransition}
      PaperProps={{ style: { backgroundColor: "transparent" } }}
    >
      <div className="flex-col-center w-full h-full bg-textcolor-black/50">
        <div style={{ transform: `scale(${scale})` }}>
          <div className="flex-col-el w-[700px] border-solid border-gray2-30 border-[16px] rounded-[16px] bg-gray2-30">
            <div className="flex-col-el justify-start w-full relative p-6 rounded-[8px] bg-white">
              <ParentCloseBtn close={handleClose} />
              {/* タイトル */}
              <div className="flex-row-el items-center gap-2 mb-4 pb-3 border-b border-outline shrink-0">
                <span className="material-symbols-outlined !text-[54px] text-red-100">person</span>
                <h2 className="text-[33px] text-textcolor-black whitespace-nowrap">
                  {getSectionTitle(section)}
                </h2>
              </div>

              {/* フォーム */}
              <form onSubmit={handleSubmit} className="flex-col-el">
                {renderSectionContent()}

                {/* ボタン */}
                <div className="flex justify-center gap-4 mt-6">
                  <button
                    type="button"
                    onClick={handleClose}
                    className="flex-row-el items-center justify-center w-[160px] py-3 bg-white border border-gray2-60 text-textcolor-black rounded-[12px] text-[24px] cursor-pointer hover:bg-gray2-20 transition-all"
                  >
                    {i18n.t("MSG_CANCEL_BTN_BY_PARENT")}
                  </button>
                  <button
                    type="submit"
                    disabled={!isValid()}
                    className={`flex-row-el items-center justify-center w-[160px] py-3 bg-ink text-white border-none rounded-[12px] text-[24px] transition-all ${
                      !isValid()
                        ? "opacity-50 cursor-not-allowed"
                        : "cursor-pointer hover:opacity-90"
                    }`}
                  >
                    {i18n.t("MSG_CONFIRM_BTN_BY_PARENT")}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      </div>
    </Dialog>
  );
};
